import ChipMultiSelect from '@/components/ChipMultiSelect';
import {
  NOTE_MAX,
  textInput,
  toggle,
  type CreateValues,
} from '@/features/reservations/model/reservation-form';
import { useSpecialRequestLabels } from '@/features/reservations/special-request-labels';
import { useT } from '@/i18n';
import { OCCASION_VALUES, SEATING_VALUES } from '@restaurant/api-contracts';
import { ReservationSource } from '@restaurant/types';
import {
  AccordionContent,
  Block,
  BlockTitle,
  List,
  ListInput,
  ListItem,
  f7,
} from 'framework7-react';
import { useController, type Control } from 'react-hook-form';

interface Props {
  control: Control<CreateValues, unknown, unknown>;
}

export default function CreateDetailsSection({ control }: Props) {
  const tr = useT();
  const { occasionLabel, seatingLabel } = useSpecialRequestLabels();
  const isIos = f7.theme === 'ios';
  const { field: source } = useController({ control, name: 'source' });
  const { field: occasion } = useController({ control, name: 'occasion' });
  const { field: seating } = useController({ control, name: 'seating' });
  const { field: internalNote } = useController({ control, name: 'internalNote' });
  const srCount = occasion.value.length + seating.value.length;

  return (
    <>
      <BlockTitle>{tr.reservations.detail.source}</BlockTitle>
      <List strongIos insetIos dividersIos>
        {[ReservationSource.PHONE, ReservationSource.WALK_IN].map((value) => (
          <ListItem
            key={value}
            radio
            radioIcon="start"
            title={tr.reservations.source[value]}
            name="source"
            checked={source.value === value}
            onChange={() => source.onChange(value)}
          />
        ))}
      </List>

      {/* Optional, secondary group - collapsed by default so the booking form
          stays short. Expands to occasion/seating chips + guest notes; the row
          shows a count of what's set. */}
      <List strongIos insetIos accordionList style={{ marginTop: 24 }}>
        <ListItem
          accordionItem
          title={tr.reservations.detail.specialRequests}
          after={srCount > 0 ? String(srCount) : undefined}
        >
          <i slot="media" className="f7-icons" style={{ fontSize: 18 }}>
            sparkles
          </i>
          <AccordionContent>
            <Block className="rsv-sr-content">
              <ChipMultiSelect
                label={tr.reservations.detail.occasion}
                options={OCCASION_VALUES}
                selected={occasion.value}
                onToggle={(o) => occasion.onChange(toggle(occasion.value, o))}
                getLabel={occasionLabel}
              />
              <div style={{ height: 18 }} />
              <ChipMultiSelect
                label={tr.reservations.detail.seating}
                options={SEATING_VALUES}
                selected={seating.value}
                onToggle={(s) => seating.onChange(toggle(seating.value, s))}
                getLabel={seatingLabel}
              />
            </Block>
          </AccordionContent>
        </ListItem>
      </List>

      <BlockTitle>{tr.reservations.notes}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListInput
          label={tr.editReservation.internalNote}
          type="textarea"
          maxlength={NOTE_MAX}
          placeholder={tr.editReservation.notePlaceholder}
          outline={!isIos}
          floatingLabel={!isIos}
          resizable
          {...textInput(internalNote)}
        />
      </List>
    </>
  );
}
